import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useClientPerms } from "../lib/useClientPerms";
import { money } from "../lib/format";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, XCircle, CheckCircle2, Lock, FileCheck2, History } from "lucide-react";
import { T4NotifyButtons } from "./ROENotify";

const RS = {
  READY: { cls: "bg-emerald-100 text-emerald-700 border-emerald-300", Icon: CheckCircle2 },
  WARNING: { cls: "bg-amber-100 text-amber-700 border-amber-300", Icon: AlertTriangle },
  BLOCKED: { cls: "bg-red-100 text-red-700 border-red-300", Icon: XCircle },
  FINALIZED: { cls: "bg-black text-white border-black", Icon: Lock },
};

function Pill({ status, testid }) {
  const s = RS[status] || RS.READY;
  const Icon = s.Icon;
  return <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border ${s.cls}`}><Icon className="h-3.5 w-3.5" />{status}</span>;
}

export default function T4YearEndPanel({ companyId, year }) {
  const [ws, setWs] = useState(null);
  const [busy, setBusy] = useState(false);
  const t4Edit = useClientPerms().canEdit("t4");
  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/t4-yearend/${companyId}/${year}`).then((r) => setWs(r.data)).catch(() => setWs(null));
  }, [companyId, year]);
  useEffect(() => { load(); }, [load]);
  if (!companyId || !ws) return null;

  const act = async (path, body, ok) => {
    setBusy(true);
    try { await api.post(`/t4-yearend/${companyId}/${year}/${path}`, body); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Action failed"); }
    finally { setBusy(false); }
  };
  const finalize = () => act("finalize", { reason: "Year-end finalized" }, "T4 year finalized");
  const amend = () => { const r = window.prompt("Amendment reason:"); if (r) act("amend", { reason: r }, "T4 amended"); };
  const cancel = () => { const r = window.prompt("Cancellation reason:"); if (r) act("cancel", { reason: r }, "T4 cancelled"); };
  const confirmFiling = () => {
    const d = window.prompt("Filing date (YYYY-MM-DD):"); if (!d) return;
    const ref = window.prompt("CRA confirmation/reference number (optional):") || "";
    act("filing", { filing_status: "FILED_CONFIRMED", filing_date: d, reference: ref }, "Filing confirmed");
  };
  const markExported = () => act("filing", { filing_status: "EXPORTED" }, "Marked exported");

  const disabled = ws.blocker_count > 0 || !ws.reconciliation.balanced;

  return (
    <div data-testid="t4-yearend-panel" className="border border-black bg-white mb-6">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-black/10">
        <ShieldCheck className="h-4 w-4" />
        <span className="font-bold tracking-tight">{year} T4 Year-End</span>
        <Pill status={ws.finalized ? "FINALIZED" : ws.readiness_status} testid="t4-readiness-status" />
        <span className="text-[11px] font-mono tabular-nums text-muted-foreground">
          {ws.slip_count} slip{ws.slip_count === 1 ? "" : "s"} · <span className="text-red-600">{ws.blocker_count}B</span> · <span className="text-amber-600">{ws.warning_count}W</span>
        </span>
        {ws.finalized && <span data-testid="t4-filing-status" className="text-[10px] font-mono uppercase tracking-widest border border-black px-2 py-0.5">{ws.filing_status}</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          {!t4Edit && <span data-testid="t4-yearend-readonly" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground self-center">View only</span>}
          {t4Edit && !ws.finalized && !ws.cancelled && (
            <button data-testid="t4-finalize-button" disabled={busy || disabled} onClick={finalize}
              className="inline-flex items-center gap-1.5 bg-black text-white px-4 py-2 text-sm font-medium hover:bg-neutral-800 disabled:opacity-40">
              <Lock className="h-4 w-4" /> Finalize
            </button>
          )}
          {t4Edit && ws.finalized && (
            <>
              <button data-testid="t4-mark-exported" disabled={busy} onClick={markExported} className="border border-black px-3 py-2 text-sm hover:bg-black hover:text-white">Mark exported</button>
              <button data-testid="t4-confirm-filing" disabled={busy} onClick={confirmFiling} className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-2 text-sm hover:bg-neutral-800"><FileCheck2 className="h-4 w-4" />Confirm filing</button>
              <button data-testid="t4-amend-button" disabled={busy} onClick={amend} className="border border-amber-500 text-amber-700 px-3 py-2 text-sm hover:bg-amber-500 hover:text-white">Amend</button>
              <button data-testid="t4-cancel-button" disabled={busy} onClick={cancel} className="border border-red-500 text-red-600 px-3 py-2 text-sm hover:bg-red-500 hover:text-white">Cancel</button>
              <T4NotifyButtons companyId={companyId} year={year} ws={ws} onDone={load} />
            </>
          )}
        </div>
      </div>

      {ws.reporting_changed && (
        <div data-testid="t4-reporting-changed" className="px-5 py-3 bg-red-50 border-b border-red-200 text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> <span className="font-semibold">Reporting changed after finalization</span> — underlying payroll changed. Review and amend the finalized T4.
        </div>
      )}

      <div className="px-5 py-4 grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Reconciliation</div>
          <div data-testid="t4-reconciliation" className={`border p-3 ${ws.reconciliation.balanced ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
            <p className={`text-sm ${ws.reconciliation.balanced ? "text-emerald-700" : "text-red-700"}`}>{ws.reconciliation.message}</p>
            {!ws.reconciliation.balanced && (
              <ul className="mt-2 text-xs space-y-1">
                {ws.reconciliation.lines.filter((l) => l.material).map((l) => (
                  <li key={l.box} className="text-red-700 tabular-nums">{l.box}: slip {money(l.slip_total)} vs payroll {money(l.payroll_total)} (Δ {money(l.difference)})</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Summary totals</div>
          <div className="border border-border p-3 text-sm tabular-nums space-y-1">
            <div className="flex justify-between"><span>Employment income</span><span>{money(ws.totals.employment_income)}</span></div>
            <div className="flex justify-between"><span>CPP / CPP2</span><span>{money(ws.totals.cpp)} / {money(ws.totals.cpp2)}</span></div>
            <div className="flex justify-between"><span>EI</span><span>{money(ws.totals.ei)}</span></div>
            <div className="flex justify-between"><span>Income tax</span><span>{money(ws.totals.income_tax)}</span></div>
          </div>
        </div>
      </div>

      {(ws.blockers.length > 0 || ws.warnings.length > 0) && !ws.finalized && (
        <div className="px-5 pb-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Readiness issues</div>
          <ul className="space-y-1.5">
            {ws.blockers.map((b, i) => <li key={`b${i}`} data-testid={`t4-blocker-${b.code}`} className="flex items-start gap-2 text-sm text-red-800"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />{b.message}</li>)}
            {ws.warnings.map((w, i) => <li key={`w${i}`} className="flex items-start gap-2 text-sm text-amber-800"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />{w.message}</li>)}
          </ul>
        </div>
      )}

      {ws.filing_confirmation && (
        <div className="px-5 pb-3 text-xs text-muted-foreground">Filed {ws.filing_confirmation.filing_date}{ws.filing_confirmation.reference ? ` · ref ${ws.filing_confirmation.reference}` : ""} by {ws.filing_confirmation.confirmed_by}</div>
      )}
      {ws.amendments?.length > 0 && (
        <div className="px-5 pb-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1.5"><History className="h-3 w-3" />Amendment history</div>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {ws.amendments.map((a, i) => <li key={i} data-testid={`t4-amendment-${i}`}>v{a.from_version}→v{a.to_version} · {a.reason} · {a.amended_by} · {a.differences?.length || 0} change(s)</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
